import { auditTime } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Subscription, Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
// BFX
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ICNAutocompleteFavoriteType,
  ICNAutocompleteFavoriteItemFormat,
} from "../models/bfx-interface-controls.enums";
import {
  ICAutocompleteClientModel,
  ICAutocompleteAccountModel,
  ICAutocompleteGenericModel,
} from "../models/bfx-interface-controls.models";
// services
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
// cubiq-services
import {
  ProfilePromiseActionService,
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  StaticDataPrivatizationType,
} from "shared/cubiq-services";
import { ProfileSettingKeys } from "shared/cubiq-services";
import { ClientAccountVisibilityType } from "shared/cubiq-services";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxUserProfileService } from "../../../../bfx-common/services/bfx-user-profile.service";
@Injectable()
export class BfxAutocompleteFavoritesProviderService {
  /**********************************************************************/
  /* DATA */
  private config: any = {
    debug: false,
    favoritesListSize: {
      clients: 3,
      accounts: 3,
      ccyPairs: 3,
      ndfCcyPairs: 3,
      bmarkCcyPairs: 3,
    },
  };

  /* these variables (control) are used in order to avoid multiple concurrent model loads(lookups), by facilitating lock mechanism  */
  private control: any = {
    modelsLoading: false,
    modelsLoadingPromiseCount: 0,
  };

  private data = {
    raw: {
      clients: new Array<string>(),
      accounts: new Array<string>(),
      ccyPairs: new Array<string>(),
      ndfCcyPairs: new Array<string>(),
      bmarkCcyPairs: new Array<string>(),
    },
    display: {
      clients: new Array<ICAutocompleteClientModel>(),
      accounts: new Array<ICAutocompleteAccountModel>(),
      ccyPairs: new Array<ICAutocompleteGenericModel>(),
      ndfCcyPairs: new Array<ICAutocompleteGenericModel>(),
      bmarkCcyPairs: new Array<ICAutocompleteGenericModel>(),
    },
    models: {
      // for Impls  not in use
      clients: new Array<CubiqTS.ClientImpl>(),
      accounts: new Array<CubiqTS.AccountImpl>(),
      ccyPairs: new Array<ICAutocompleteGenericModel>(), // added just for consistency
      ndfCcyPairs: new Array<ICAutocompleteGenericModel>(),
      bmarkCcyPairs: new Array<ICAutocompleteGenericModel>(),
    },
  };

  /**********************************************************************/
  /* SUBSCRIPTIONs */
  private configurationSubscription: Subscription;
  private saveSubject = new Subject<void>();
  private saveSubjectAuditInit = false;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private _configService: BfxConfigService,
    protected _translationService: TranslateService,
    protected _profileService: ProfilePromiseActionService,
    private _clientLookupService: ClientPromiseLookupService,
    private _accountLookupService: AccountPromiseLookupService,
    private _ccyPairLookupService: CcyPairPromiseLookupService,
    protected userProfileService: BfxUserProfileService,
  ) {
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  private initialize(): void {
    this.subscribeToConfiguration();
    this.loadFavorites();

    if (this.saveSubjectAuditInit === false) {
      this.saveSubjectAuditInit = true;
      let at = 90000 + (Math.floor(Math.random() * Math.floor(60000)) - 30000);

      logger.out.info(`User profile favorites saving will be delayed upto ${at}ms`);
      this.saveSubject
        .pipe(
          // 90 +/- 30
          auditTime(at),
        )
        .subscribe(() => {
          // request save on server
          let profileConfigKey: string = ProfileSettingKeys.userProfileFavoritesAutocomplete;
          let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
          profileSettings.push(
            this.userProfileService.createProfileSettingObject(
              profileConfigKey.toLowerCase(),
              JSON.stringify(this.data.raw),
            ),
          );
          this._profileService.updateProfile(profileSettings, false).then(() => {
            logger.out.info("User profile favorites updated.");
            // logger.out.info('User profile favorites updated = ', this.data.raw);
          });
        });
    }
  }

  /**********************************************************************/
  /* Profile TEst */

  private resetFavorites(): void {
    let clientsSample = []; // 'BBC', 'Apple', 'Barracuda FX'];
    let accountsSample = [];
    let ccyPairsSample = []; // 'USDEUR'];
    let ndfCcyPairsSample = []; // 'USDEUR'];
    let bmarkCcyPairsSample = []; // 'USDEUR'];

    this.data.raw.clients = clientsSample;
    this.data.raw.accounts = accountsSample;
    this.data.raw.ccyPairs = ccyPairsSample;
    this.data.raw.ndfCcyPairs = ndfCcyPairsSample;
    this.data.raw.bmarkCcyPairs = bmarkCcyPairsSample;

    this.saveFavorites();
    this.loadFavoriteModels();
  }

  /**********************************************************************/
  /* USER PROFILE METHODS */

  public getFavoritesListSize(favoriteItemType: ICNAutocompleteFavoriteType): number {
    switch (favoriteItemType) {
      case ICNAutocompleteFavoriteType.CLIENT:
        return this.config.favoritesListSize.clients;
      case ICNAutocompleteFavoriteType.ACCOUNT:
        return this.config.favoritesListSize.accounts;
      case ICNAutocompleteFavoriteType.CCY_PAIR:
        return this.config.favoritesListSize.ccyPairs;
      case ICNAutocompleteFavoriteType.CCY_PAIR_NDF:
        return this.config.favoritesListSize.ndfCcyPairs;
      case ICNAutocompleteFavoriteType.CCY_PAIR_BMARK:
        return this.config.favoritesListSize.bmarkCcyPairs;
      default:
    }
  }
  /**
   * RAW Favorites arrays should behave like a queue
   * - New items are added at the start of the favorites array
   * - Old items are removed form the end of the array
   */
  public addFavoriteItem(favoriteItemType: ICNAutocompleteFavoriteType, itemId: string): void {
    if (
      favoriteItemType != null &&
      itemId != null &&
      typeof itemId === "string" &&
      itemId.length > 0
    ) {
      switch (favoriteItemType) {
        case ICNAutocompleteFavoriteType.CLIENT:
          let clientIndex: number = this.data.raw.clients.indexOf(itemId);
          if (clientIndex === 0) {
            // the element that needs to be added is already in the right place, changes to raw or models (already loaded) not necessary
            return;
          }
          if (clientIndex > 0) {
            this.data.raw.clients.splice(clientIndex, 1);
          }
          while (this.data.raw.clients.length >= this.config.favoritesListSize.clients) {
            this.data.raw.clients.pop();
          }
          this.data.raw.clients.unshift(itemId);
          break;
        case ICNAutocompleteFavoriteType.ACCOUNT:
          let accountIndex: number = this.data.raw.accounts.indexOf(itemId);
          if (accountIndex >= 0) {
            this.data.raw.accounts.splice(accountIndex, 1);
          }
          while (this.data.raw.accounts.length >= this.config.favoritesListSize.accounts) {
            this.data.raw.accounts.pop();
          }
          this.data.raw.accounts.unshift(itemId);
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR:
          let ccyPairIndex: number = this.data.raw.ccyPairs.indexOf(itemId);
          if (ccyPairIndex >= 0) {
            this.data.raw.ccyPairs.splice(ccyPairIndex, 1);
          }
          while (this.data.raw.ccyPairs.length >= this.config.favoritesListSize.ccyPairs) {
            this.data.raw.ccyPairs.pop();
          }
          this.data.raw.ccyPairs.unshift(itemId);
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR_NDF:
          let ndfCcyPairIndex: number = this.data.raw.ndfCcyPairs.indexOf(itemId);
          if (ndfCcyPairIndex >= 0) {
            this.data.raw.ndfCcyPairs.splice(ndfCcyPairIndex, 1);
          }
          while (this.data.raw.ndfCcyPairs.length >= this.config.favoritesListSize.ndfCcyPairs) {
            this.data.raw.ndfCcyPairs.pop();
          }
          this.data.raw.ndfCcyPairs.unshift(itemId);
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR_BMARK: {
          let bmarkCcyPairIndex: number = this.data.raw.bmarkCcyPairs.indexOf(itemId);
          if (bmarkCcyPairIndex >= 0) {
            this.data.raw.bmarkCcyPairs.splice(bmarkCcyPairIndex, 1);
          }
          while (
            this.data.raw.bmarkCcyPairs.length >= this.config.favoritesListSize.bmarkCcyPairs
          ) {
            this.data.raw.bmarkCcyPairs.pop();
          }
          this.data.raw.bmarkCcyPairs.unshift(itemId);
          break;
        }
        default:
          logger.out.error(
            "ERROR: BfxAutocompleteFavoritesProviderService.addFavoriteItem(): Invalid favorites type!",
          );
          break;
      }
      this.saveFavorites();
      this.loadFavoriteModels();
    }
  }

  public getFavoriteItems(
    favoriteType: ICNAutocompleteFavoriteType,
    itemFormat: ICNAutocompleteFavoriteItemFormat,
  ): Array<any> {
    if (favoriteType != null && itemFormat != null) {
      switch (favoriteType) {
        case ICNAutocompleteFavoriteType.CLIENT:
          switch (itemFormat) {
            case ICNAutocompleteFavoriteItemFormat.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.clients));
            case ICNAutocompleteFavoriteItemFormat.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.clients));
            case ICNAutocompleteFavoriteItemFormat.DISPLAY:
              return this.castToClientDisplay(
                JSON.parse(JSON.stringify(this.data.display.clients)),
              );
            default:
              return null;
          }
        case ICNAutocompleteFavoriteType.ACCOUNT:
          switch (itemFormat) {
            case ICNAutocompleteFavoriteItemFormat.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.accounts));
            case ICNAutocompleteFavoriteItemFormat.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.accounts));
            case ICNAutocompleteFavoriteItemFormat.DISPLAY:
              return this.castToAccountDisplay(
                JSON.parse(JSON.stringify(this.data.display.accounts)),
              );
            default:
              return null;
          }
        case ICNAutocompleteFavoriteType.CCY_PAIR:
          switch (itemFormat) {
            case ICNAutocompleteFavoriteItemFormat.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.ccyPairs));
            case ICNAutocompleteFavoriteItemFormat.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.ccyPairs));
            case ICNAutocompleteFavoriteItemFormat.DISPLAY:
              return this.castToCcyPairDisplay(
                JSON.parse(JSON.stringify(this.data.display.ccyPairs)),
              );
            default:
              return null;
          }
        case ICNAutocompleteFavoriteType.CCY_PAIR_NDF:
          switch (itemFormat) {
            case ICNAutocompleteFavoriteItemFormat.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.ndfCcyPairs));
            case ICNAutocompleteFavoriteItemFormat.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.ndfCcyPairs));
            case ICNAutocompleteFavoriteItemFormat.DISPLAY:
              return this.castToCcyPairDisplay(
                JSON.parse(JSON.stringify(this.data.display.ndfCcyPairs)),
              );
            default:
              return null;
          }
        case ICNAutocompleteFavoriteType.CCY_PAIR_BMARK: {
          switch (itemFormat) {
            case ICNAutocompleteFavoriteItemFormat.MODEL: {
              return JSON.parse(JSON.stringify(this.data.models.bmarkCcyPairs));
            }
            case ICNAutocompleteFavoriteItemFormat.RAW: {
              return JSON.parse(JSON.stringify(this.data.raw.bmarkCcyPairs));
            }
            case ICNAutocompleteFavoriteItemFormat.DISPLAY: {
              return this.castToCcyPairDisplay(
                JSON.parse(JSON.stringify(this.data.display.bmarkCcyPairs)),
              );
            }
            default: {
              return null;
            }
          }
        }
        default:
          return null;
      }
    }
  }

  private loadFavorites(): void {
    let profileConfigKey: string = ProfileSettingKeys.userProfileFavoritesAutocomplete;
    profileConfigKey = profileConfigKey.toLowerCase();

    this._profileService.getProfileConfig(profileConfigKey).then((rawValues: string) => {
      if (rawValues != null) {
        let parsedValues = JSON.parse(rawValues);
        /// CLIENTS
        if (parsedValues.clients != null && parsedValues.clients instanceof Array) {
          this.data.raw.clients = parsedValues.clients;
        }
        // ACCOUNTS
        if (parsedValues.accounts != null && parsedValues.accounts instanceof Array) {
          this.data.raw.accounts = parsedValues.accounts;
        }
        // CCY-PAIRS
        if (parsedValues.ccyPairs != null && parsedValues.ccyPairs instanceof Array) {
          this.data.raw.ccyPairs = parsedValues.ccyPairs;
        }
        if (parsedValues.ndfCcyPairs != null && parsedValues.ndfCcyPairs instanceof Array) {
          this.data.raw.ndfCcyPairs = parsedValues.ndfCcyPairs;
        }
        if (parsedValues.bmarkCcyPairs != null && parsedValues.bmarkCcyPairs instanceof Array) {
          this.data.raw.bmarkCcyPairs = parsedValues.bmarkCcyPairs;
          if (this.data.raw.bmarkCcyPairs && this.data.raw.bmarkCcyPairs.length > 0) {
            // if its too long, trim it
            let bmarkFavsMaxLen: number = this.getFavoritesListSize(
              ICNAutocompleteFavoriteType.CCY_PAIR_BMARK,
            );
            if (bmarkFavsMaxLen < this.data.raw.bmarkCcyPairs.length) {
              this.data.raw.bmarkCcyPairs = this.data.raw.bmarkCcyPairs.splice(
                0,
                bmarkFavsMaxLen + 1,
              );
            }
          }
        }
        this.loadFavoriteModels();
      } else {
        logger.out.warn(
          `${this.constructor.name}: --> loadFavorites() Profile returned null!: %O`,
          rawValues,
        );
      }
    });
  }

  private saveFavorites(): void {
    this.saveSubject.next();
  }
  /**********************************************************************/
  /* Model Loading */

  private loadFavoriteModels(): void {
    if (!this.control.modelsLoading) {
      this.ctrlModelsLoadingLock();
      /* CLIENT Models */
      this.data.models.clients = new Array<CubiqTS.ClientImpl>();
      this.data.display.clients = new Array<ICAutocompleteClientModel>();
      this.data.raw.clients.slice(0).forEach((item) => {
        this.clientLookup(item)
          .then((result: ICAutocompleteClientModel | null) => {
            if (result == null) {
              let removeIndex = this.data.raw.clients.indexOf(item);
              this.data.raw.clients.splice(removeIndex, 1);
              this.ctrlModelsLoadingUnlock();
            } else {
              let duplicateClientIndex = this.data.display.clients.findIndex((clientModel) => {
                return clientModel.value === item;
              });
              if (duplicateClientIndex < 0) {
                this.data.display.clients.push(result);
                this.ctrlModelsLoadingUnlock();
              }
            }
          })
          .catch((ex) => {
            logger.out.warn("WARNING: ", ex, " - Error handling started!");
            this.handleFavoritesEntryError(ICNAutocompleteFavoriteType.CLIENT, item);
            this.ctrlModelsLoadingUnlock();
          });
      });
      /* ACCOUNT Models */
      this.data.models.accounts = new Array<CubiqTS.AccountImpl>();
      this.data.display.accounts = new Array<ICAutocompleteAccountModel>();
      this.data.raw.accounts.slice(0).forEach((item) => {
        this.accountLookup(item)
          .then((result: ICAutocompleteAccountModel | null) => {
            if (result == null) {
              let removeIndex = this.data.raw.clients.indexOf(item);
              this.data.raw.clients.splice(removeIndex, 1);
              this.ctrlModelsLoadingUnlock();
            } else {
              let duplicateAccountsIndex = this.data.display.accounts.findIndex((accountModel) => {
                return accountModel.value === item;
              });
              if (duplicateAccountsIndex < 0) {
                this.data.display.accounts.push(result);
                this.ctrlModelsLoadingUnlock();
              }
            }
          })
          .catch((ex) => {
            logger.out.warn("WARNING: ", ex);
            this.handleFavoritesEntryError(ICNAutocompleteFavoriteType.ACCOUNT, item);
            this.ctrlModelsLoadingUnlock();
          });
      });
      /* CCY_PAIRS Models */
      this.data.display.ccyPairs = new Array<ICAutocompleteGenericModel>();
      this.data.raw.ccyPairs.slice(0).forEach((item) => {
        this.ccyPairValidate(item).then((result) => {
          if (result != null) {
            this.data.display.ccyPairs.push(new ICAutocompleteGenericModel(item, item));
            this.ctrlModelsLoadingUnlock();
          } else {
            logger.out.warn("WARNING: invalid ccy pair [" + item + "]");
            this.handleFavoritesEntryError(ICNAutocompleteFavoriteType.CCY_PAIR, item);
            this.ctrlModelsLoadingUnlock();
          }
        });
      });
      /* NDF CCY_PAIRS Models */
      this.data.display.ndfCcyPairs = new Array<ICAutocompleteGenericModel>();
      this.data.raw.ndfCcyPairs.slice(0).forEach((item) => {
        this.ndfCcyPairValidate(item).then((result) => {
          if (result != null) {
            this.data.display.ndfCcyPairs.push(new ICAutocompleteGenericModel(item, item));
            this.ctrlModelsLoadingUnlock();
          } else {
            logger.out.warn("WARNING: invalid ndf ccy pair [" + item + "]");
            this.handleFavoritesEntryError(ICNAutocompleteFavoriteType.CCY_PAIR_NDF, item);
            this.ctrlModelsLoadingUnlock();
          }
        });
      });

      /* BMARK CCY_PAIRS Models */
      this.data.display.bmarkCcyPairs = new Array<ICAutocompleteGenericModel>();
      this.data.raw.bmarkCcyPairs.slice(0).forEach((item) => {
        this.bmarkCcyPairValidate(item).then((result) => {
          if (result != null) {
            this.data.display.bmarkCcyPairs.push(new ICAutocompleteGenericModel(item, item));
            this.ctrlModelsLoadingUnlock();
          } else {
            logger.out.warn("WARNING: invalid bmark ccy pair [" + item + "]");
            this.handleFavoritesEntryError(ICNAutocompleteFavoriteType.CCY_PAIR_BMARK, item);
            this.ctrlModelsLoadingUnlock();
          }
        });
      });
    }
  }
  /* Locking mechanism for loadFavoriteModels() to avoid concurrent loading */
  private ctrlModelsLoadingLock(): void {
    this.control.modelsLoading = true;
    setTimeout(() => {
      // fall back - unlock after 10 secs
      if (this.control.modelsLoading) {
        logger.out.warn(
          "WARNING: manual unlock of the favorite model loading procedure in effect!",
        );
        this.control.modelsLoading = false;
        this.control.modelsLoadingPromiseCount = 0;
      }
    }, 10000);
  }

  private ctrlModelsLoadingUnlock(): void {
    this.control.modelsLoadingPromiseCount++;
    let loadingSize =
      this.data.raw.clients.length +
      this.data.raw.accounts.length +
      this.data.raw.ccyPairs.length +
      this.data.raw.ndfCcyPairs.length +
      this.data.raw.bmarkCcyPairs.length;
    if (this.control.modelsLoadingPromiseCount >= loadingSize) {
      this.control.modelsLoading = false;
      this.control.modelsLoadingPromiseCount = 0;
    }
  }
  /**********************************************************************/
  /* Lookups & Validators */

  private clientLookup(clientId: string): Promise<ICAutocompleteClientModel> {
    return new Promise((res, rej) => {
      this._clientLookupService.getActiveClient(clientId).then((result: CubiqTS.ClientImpl) => {
        if (result != null && result instanceof CubiqTS.ClientImpl) {
          if (!ClientAccountVisibilityType.isFull(result)) {
            // lookup worked, but result is unusable for entry
            res(null);
          } else {
            res(
              new ICAutocompleteClientModel(
                result.clientId,
                result.clientId,
                result.clientId,
                result.clientName,
                StaticDataPrivatizationType.hasPrivatization(result),
                result.autoFillEnabled,
                result.branchCd,
              ),
            );
          }
        } else {
          rej("Client lookup result is null or wrong type [required-type: ClientImpl]!");
        }
      });
    });
  }

  private accountLookup(accountId: string): Promise<ICAutocompleteAccountModel> {
    return new Promise((res, rej) => {
      this._accountLookupService.getActiveAccount(accountId).then((result: CubiqTS.AccountImpl) => {
        if (result != null && result instanceof CubiqTS.AccountImpl) {
          if (!ClientAccountVisibilityType.isFull(result)) {
            // lookup worked, but result is unusable for entry
            res(null);
          } else {
            res(
              new ICAutocompleteAccountModel(
                result.id,
                result.id,
                result.id,
                result.name,
                result.bookingCode,
                result.contact,
                StaticDataPrivatizationType.hasPrivatization(result),
              ),
            );
          }
        } else {
          rej("Account lookup result is null or wrong type [required-type: AccountImpl]!");
        }
      });
    });
  }

  private ccyPairValidate(ccyPairCode: string): Promise<boolean> {
    return new Promise((res, rej) => {
      let currencyPairLookup: CcyPairLookupService = this._ccyPairLookupService.getBase();
      let currencyPairModel = currencyPairLookup.getItem(ccyPairCode);

      if (currencyPairModel != null) {
        res(true);
      } else {
        currencyPairModel = currencyPairLookup.getCcyPair(
          ccyPairCode.substr(3, 3),
          ccyPairCode.substr(0, 3),
        );
        if (currencyPairModel != null && currencyPairModel.allowInvertsFg) {
          res(true);
        } else {
          res(false);
        }
      }
    });
  }

  private ndfCcyPairValidate(ccyPairCode: string): Promise<boolean> {
    return new Promise((res, rej) => {
      let currencyPairLookup: CcyPairLookupService = this._ccyPairLookupService.getBase();
      let currencyPairModel = currencyPairLookup.getItem(ccyPairCode);

      if (currencyPairModel != null) {
        if (
          currencyPairModel.ndfEnabledFg &&
          (currencyPairModel.allowNdfFwdTriggerFg || currencyPairModel.allowNdfSpotTriggerFg)
        ) {
          res(true);
        } else {
          res(false);
        }
      } else {
        currencyPairModel = currencyPairLookup.getCcyPair(
          ccyPairCode.substr(3, 3),
          ccyPairCode.substr(0, 3),
        );
        if (currencyPairModel != null && currencyPairModel.allowInvertsFg) {
          if (
            currencyPairModel.ndfEnabledFg &&
            (currencyPairModel.allowNdfFwdTriggerFg || currencyPairModel.allowNdfSpotTriggerFg)
          ) {
            res(true);
          } else {
            res(false);
          }
        } else {
          res(false);
        }
      }
    });
  }

  private bmarkCcyPairValidate(ccyPairCode: string): Promise<boolean> {
    return new Promise((res, rej) => {
      let currencyPairLookup: CcyPairLookupService = this._ccyPairLookupService.getBase();
      let currencyPairModel = currencyPairLookup.getItem(ccyPairCode);

      if (currencyPairModel != null) {
        if (currencyPairModel.allowBmarkSpotOrdersFg || currencyPairModel.allowBmarkFwdOrdersFg) {
          res(true);
        } else {
          res(false);
        }
      } else {
        currencyPairModel = currencyPairLookup.getCcyPair(
          ccyPairCode.substr(3, 3),
          ccyPairCode.substr(0, 3),
        );
        if (currencyPairModel != null && currencyPairModel.allowInvertsFg) {
          if (currencyPairModel.allowBmarkSpotOrdersFg || currencyPairModel.allowBmarkFwdOrdersFg) {
            res(true);
          } else {
            res(false);
          }
        } else {
          res(false);
        }
      }
    });
  }

  /**********************************************************************/
  /* Error handling */

  private handleFavoritesEntryError(
    favoriteType: ICNAutocompleteFavoriteType,
    itemId: string,
  ): void {
    let index;

    if (itemId != null && itemId.length > 0) {
      switch (favoriteType) {
        case ICNAutocompleteFavoriteType.CLIENT:
          index = this.data.raw.clients.indexOf(itemId);
          if (index >= 0 && this.data.raw.clients.length > index) {
            this.data.raw.clients.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case ICNAutocompleteFavoriteType.ACCOUNT:
          index = this.data.raw.accounts.indexOf(itemId);
          if (index >= 0 && this.data.raw.accounts.length > index) {
            this.data.raw.accounts.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR:
          index = this.data.raw.ccyPairs.indexOf(itemId);
          if (index >= 0 && this.data.raw.ccyPairs.length > index) {
            this.data.raw.ccyPairs.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR_NDF:
          index = this.data.raw.ndfCcyPairs.indexOf(itemId);
          if (index >= 0 && this.data.raw.ndfCcyPairs.length > index) {
            this.data.raw.ndfCcyPairs.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case ICNAutocompleteFavoriteType.CCY_PAIR_BMARK: {
          index = this.data.raw.bmarkCcyPairs.indexOf(itemId);
          if (index >= 0 && this.data.raw.bmarkCcyPairs.length > index) {
            this.data.raw.bmarkCcyPairs.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        }
        default:
          logger.out.error("ERROR: Undetermined item type");
          break;
      }
    } else {
      logger.out.error("ERROR: Error handling procedure FAIL - Unknown itemId!");
    }
  }

  /**********************************************************************/
  /* Error handling */
  private castToAccountDisplay(accounts: Array<any>): Array<ICAutocompleteAccountModel> {
    let result: Array<ICAutocompleteAccountModel> = new Array<ICAutocompleteAccountModel>();
    accounts.forEach((item) => {
      let itemModel = new ICAutocompleteAccountModel(
        item.value,
        item.label,
        item.id,
        item.name,
        item.booking,
        item.contact,
      );
      itemModel.smartLabel = item.smartLabel;
      //itemModel.isAutoFillOrBreak = item.isAutoFillOrBreak;
      result.push(itemModel);
    });
    return result;
  }

  private castToClientDisplay(
    clients: Array<ICAutocompleteClientModel>,
  ): Array<ICAutocompleteClientModel> {
    let result: Array<ICAutocompleteClientModel> = new Array<ICAutocompleteClientModel>();
    clients.forEach((item) => {
      result.push(
        new ICAutocompleteClientModel(
          item.value,
          item.label,
          item.id,
          item.name,
          item.hasPrivatization,
          item.isAutoFillEnabled,
          item.branchCd,
        ),
      );
    });
    return result;
  }

  private castToCcyPairDisplay(ccyPairs: Array<any>): Array<ICAutocompleteGenericModel> {
    let result: Array<ICAutocompleteGenericModel> = new Array<ICAutocompleteGenericModel>();
    ccyPairs.forEach((item) => {
      result.push(new ICAutocompleteGenericModel(item.value, item.label));
    });
    return result;
  }
}
